import {
    API,
    LOGIN,
    FORGOT_PASSWORD,
    CHANGE_PASSWORD,
    USERS,
    ADMIN_CONTEXT,
    TWO_FACTOR
} from '../../constants/actions';
import { getToken } from '../../services/local.storage';
import { ERROR_TEXTS } from '../../constants/texts/errors';
import { LOCATION_CHANGE } from 'react-router-redux';
import { userApiDataToState } from '../../selectors/authentication';

const initialState = {
    authenticated: !!getToken(),
    user: {
        email: '',
        fetched: false,
        roles: [],
        permissions: [],
        contextRole: { permissions: [] }
    },
    form: {
        id: '',
        email: '',
        password: '',
        newPassword: '',
        oldPassword: '',
        newPasswordConfirmation: '',
        error: '',
        rememberMe: false,
        submitting: false
    },
    twoFactor: {
        requiresSetup: false,
        requiresVerification: false,
        setupToken: null,
        tempLoginToken: null,
        qrCode: null,
        secret: null,
        code: '',
        recoveryCode: '',
        useRecoveryCode: false,
        recoveryCodes: null,
        showRecoveryCodes: false,
        remainingRecoveryCodes: null,
        usedRecoveryCode: false,
        submitting: false,
        error: '',
        tempCredentials: null,
        isResettingTwoFactor: false,
        rememberMe: false
    }
};

function authentication(state = initialState, action) {
    switch (action.type) {
        case API.UNAUTHORIZED:
            return initialState;
        case LOGIN.REFRESH:
            return {
                ...state,
                form: { ...initialState.form }
            };
        case LOGIN.INPUT:
        case FORGOT_PASSWORD.INPUT:
        case CHANGE_PASSWORD.INPUT:
            return { ...state, form: { ...state.form, [action.key]: action.value, error: '' } };
        case FORGOT_PASSWORD.FAILED:
        case LOGIN.FAILED:
        case CHANGE_PASSWORD.FAILED:
            return {
                ...state,
                form: {
                    ...state.form,
                    submitting: false,
                    error: action.error || ERROR_TEXTS[action.type]
                }
            };
        case FORGOT_PASSWORD.SUBMIT:
        case CHANGE_PASSWORD.SUBMIT:
        case LOGIN.SUBMIT:
            return { ...state, form: { ...state.form, submitting: true, error: '' } };
        case CHANGE_PASSWORD.SUCCESS:
            return {
                ...state,
                authenticated: true,
                form: initialState.form
            };
        case LOGIN.SUCCESS:
        case USERS.FETCH_OK:
            return {
                ...state,
                authenticated: true,
                form: initialState.form,
                user: {
                    ...state.user,
                    ...userApiDataToState(action.user),
                    fetched: true
                }
            };
        case USERS.UPDATE:
            return {
                ...state,
                user: {
                    ...state.user,
                    [action.field]: action.value
                }
            };
        case ADMIN_CONTEXT.SET:
            return {
                ...state,
                user: {
                    ...state.user,
                    contextRole: action.contextRole
                }
            };
        case LOCATION_CHANGE:
            return { ...state, form: initialState.form };
        case FORGOT_PASSWORD.SUCCESS:
            return { ...state, form: initialState.form, user: { ...state.user, email: action.email } };
        case LOGIN.CANCEL:
            return { ...initialState, authenticated: false };
        // Two-Factor Authentication cases
        case TWO_FACTOR.SET:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    requiresSetup: action.requiresSetup || false,
                    requiresVerification: action.requiresVerification || false,
                    setupToken: action.setupToken || null,
                    tempLoginToken: action.tempLoginToken || null,
                    tempCredentials: action.tempCredentials || null,
                    rememberMe: action.rememberMe !== undefined ? action.rememberMe : state.twoFactor.rememberMe
                }
            };
        case TWO_FACTOR.INPUT:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    [action.key]: action.value,
                    error: ''
                }
            };
        case TWO_FACTOR.FETCH_START:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: true,
                    error: ''
                }
            };
        case TWO_FACTOR.FETCH_OK:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: false,
                    qrCode: action.qrCode || state.twoFactor.qrCode,
                    secret: action.secret || state.twoFactor.secret
                }
            };
        case TWO_FACTOR.SUBMIT:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: true,
                    error: ''
                }
            };
        case TWO_FACTOR.SUCCESS:
            return {
                ...state,
                authenticated: true,
                form: initialState.form,
                twoFactor: {
                    ...initialState.twoFactor,
                    // Preserve recovery codes if they were set (for showing modal after 2FA setup)
                    recoveryCodes: state.twoFactor.recoveryCodes,
                    showRecoveryCodes: state.twoFactor.showRecoveryCodes
                },
                user: {
                    ...state.user,
                    ...userApiDataToState(action.user),
                    fetched: true
                }
            };
        case TWO_FACTOR.FAILED:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: false,
                    error: action.error || ERROR_TEXTS[action.type] || 'Invalid code'
                }
            };
        case TWO_FACTOR.RESET:
            return {
                ...state,
                twoFactor: initialState.twoFactor
            };
        case TWO_FACTOR.SHOW_RECOVERY_CODES:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: false,
                    recoveryCodes: action.recoveryCodes,
                    showRecoveryCodes: true
                }
            };
        case TWO_FACTOR.HIDE_RECOVERY_CODES:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: false,
                    showRecoveryCodes: false,
                    recoveryCodes: null
                }
            };
        case TWO_FACTOR.TOGGLE_RECOVERY_MODE:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    useRecoveryCode: !state.twoFactor.useRecoveryCode,
                    error: '',
                    code: '',
                    recoveryCode: ''
                }
            };
        case TWO_FACTOR.RECOVERY_LOGIN_SUCCESS:
            return {
                ...state,
                authenticated: true,
                form: initialState.form,
                twoFactor: {
                    ...initialState.twoFactor,
                    usedRecoveryCode: true,
                    remainingRecoveryCodes: action.remainingRecoveryCodes
                },
                user: {
                    ...state.user,
                    ...userApiDataToState(action.user),
                    fetched: true
                }
            };
        case TWO_FACTOR.STATUS_FETCHED:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    remainingRecoveryCodes: action.remainingRecoveryCodes,
                    enabled: action.enabled
                }
            };
        case TWO_FACTOR.RESET_2FA_START:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: true,
                    error: '',
                    isResettingTwoFactor: false
                }
            };
        case TWO_FACTOR.RESET_2FA_OK:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: false,
                    isResettingTwoFactor: true,
                    qrCode: action.qrCode,
                    secret: action.secret,
                    code: '',
                    error: ''
                }
            };
        case TWO_FACTOR.RESET_2FA_CANCEL:
            return {
                ...state,
                twoFactor: {
                    ...state.twoFactor,
                    submitting: false,
                    isResettingTwoFactor: false,
                    qrCode: null,
                    secret: null,
                    code: '',
                    error: ''
                }
            };
        default:
            return state;
    }
}

export default authentication;
